import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { 
  Fingerprint, RefreshCw, Wifi, WifiOff, CheckCircle2, 
  Clock, AlertTriangle, Send, Trash2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function EmployeeBiometricSync({ employee, onUpdate }) {
  const [syncStatus, setSyncStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (employee?.id) {
      fetchSyncStatus();
    }
  }, [employee?.id]);

  const fetchSyncStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/employees/${employee.id}/sync-status`);
      setSyncStatus(response.data);
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Error fetching sync status:', error);
      // If no devices, show empty state
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (selectedDevices.length === 0) {
      toast.error('Veuillez sélectionner au moins un pointeur');
      return;
    }

    if (!employee.biometric_id) {
      toast.error("L'employé n'a pas d'ID biométrique configuré");
      return;
    }

    setSyncing(true);
    try {
      const response = await api.post(`/employees/${employee.id}/sync`, {
        device_ids: selectedDevices
      });
      
      toast.success('Synchronisation lancée avec succès');
      setSelectedDevices([]);
      fetchSyncStatus();
      
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncAll = async () => {
    if (!employee.biometric_id) {
      toast.error("L'employé n'a pas d'ID biométrique configuré");
      return;
    }

    setSyncing(true);
    try {
      const response = await api.post(`/employees/${employee.id}/sync-all`);
      toast.success('Synchronisation vers tous les pointeurs lancée');
      fetchSyncStatus();
      
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleRemove = async (deviceSerial) => {
    setRemoving(true);
    try {
      await api.post(`/employees/${employee.id}/unsync`, {
        device_ids: [deviceSerial]
      });
      
      toast.success('Suppression du pointeur lancée');
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setRemoving(false);
    }
  };

  const toggleDevice = (deviceSerial) => {
    setSelectedDevices(prev => 
      prev.includes(deviceSerial)
        ? prev.filter(d => d !== deviceSerial)
        : [...prev, deviceSerial]
    );
  };

  const selectAllDevices = () => {
    const notSyncedDevices = devices
      .filter(d => d.status === 'not_synced')
      .map(d => d.device_serial);
    setSelectedDevices(notSyncedDevices);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'synced':
        return (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Synchronisé
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            En attente
          </Badge>
        );
      case 'removing':
        return (
          <Badge className="bg-orange-100 text-orange-800">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Suppression...
          </Badge>
        );
      case 'error':
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-800">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Erreur
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-100 text-slate-600">
            Non synchronisé
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  // Check if employee has biometric_id
  if (!employee?.biometric_id) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5" />
            Synchronisation Biométrique
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800">ID Biométrique requis</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Cet employé n'a pas d'ID biométrique configuré. 
                  Veuillez d'abord lui attribuer un ID biométrique dans ses informations personnelles 
                  avant de pouvoir le synchroniser vers les pointeuses.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No devices configured
  if (devices.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5" />
            Synchronisation Biométrique
          </CardTitle>
          <CardDescription>
            ID Biométrique: <span className="font-mono font-semibold">{employee.biometric_id}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <WifiOff className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h4 className="font-medium text-slate-700">Aucun pointeur configuré</h4>
            <p className="text-sm text-slate-500 mt-1">
              Il n'y a pas de pointeur ZKTeco actif pour cette entreprise.
              Veuillez d'abord configurer un pointeur dans la section "Pointeuses ZKTeco".
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const notSyncedDevices = devices.filter(d => d.status === 'not_synced');
  const syncedDevices = devices.filter(d => ['synced', 'pending'].includes(d.status));

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5" />
              Synchronisation Biométrique
            </CardTitle>
            <CardDescription className="mt-1">
              ID Biométrique (PIN): <span className="font-mono font-semibold text-slate-900">{employee.biometric_id}</span>
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchSyncStatus}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Actualiser
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Explanation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">Comment ça fonctionne</h4>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>Sélectionnez les pointeuses sur lesquelles l'employé doit pouvoir pointer</li>
            <li>Cliquez sur "Synchroniser" pour envoyer les informations au pointeur</li>
            <li>L'employé devra ensuite enregistrer son empreinte <strong>directement sur la pointeuse</strong></li>
            <li>Une fois l'empreinte enregistrée, il pourra pointer normalement</li>
          </ol>
        </div>

        {/* Synced Devices */}
        {syncedDevices.length > 0 && (
          <div>
            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              Pointeuses synchronisées ({syncedDevices.length})
            </h4>
            <div className="space-y-2">
              {syncedDevices.map((device) => (
                <div 
                  key={device.device_serial} 
                  className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Wifi className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-slate-900">{device.device_name}</p>
                      <p className="text-sm text-slate-500">{device.location} • {device.device_serial}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(device.status)}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleRemove(device.device_serial)}
                      disabled={removing}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Not Synced Devices */}
        {notSyncedDevices.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <WifiOff className="w-4 h-4 text-slate-400" />
                Pointeuses disponibles ({notSyncedDevices.length})
              </h4>
              {notSyncedDevices.length > 1 && (
                <Button variant="ghost" size="sm" onClick={selectAllDevices}>
                  Tout sélectionner
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {notSyncedDevices.map((device) => (
                <div 
                  key={device.device_serial} 
                  className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedDevices.includes(device.device_serial)
                      ? 'bg-indigo-50 border-indigo-300'
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                  }`}
                  onClick={() => toggleDevice(device.device_serial)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={selectedDevices.includes(device.device_serial)}
                      onCheckedChange={() => toggleDevice(device.device_serial)}
                    />
                    <div>
                      <p className="font-medium text-slate-900">{device.device_name}</p>
                      <p className="text-sm text-slate-500">{device.location} • {device.device_serial}</p>
                    </div>
                  </div>
                  {getStatusBadge(device.status)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          {notSyncedDevices.length > 0 && (
            <>
              <Button 
                onClick={handleSync}
                disabled={selectedDevices.length === 0 || syncing}
                className="flex-1"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Synchroniser ({selectedDevices.length})
              </Button>
              
              {devices.length > 1 && (
                <Button 
                  variant="outline"
                  onClick={handleSyncAll}
                  disabled={syncing}
                >
                  Sync. tous les pointeurs
                </Button>
              )}
            </>
          )}
          
          {notSyncedDevices.length === 0 && syncedDevices.length > 0 && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Employé synchronisé sur tous les pointeurs</span>
            </div>
          )}
        </div>

        {/* Warning about fingerprint */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Empreinte digitale requise</h4>
              <p className="text-sm text-amber-700 mt-1">
                Après la synchronisation, l'employé devra se rendre physiquement devant chaque pointeuse 
                pour y enregistrer son empreinte digitale. Cette étape ne peut pas être faite à distance.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
